/**
 * InlineWhereGroup — renders an explicit (parenthesised) WHERE/HAVING group
 * inside the Inline pill view as a subtle, connector-tinted box whose children
 * are laid out as SEPARATE, individually-editable pills joined by their
 * connector — instead of one opaque freeform-text pill (the pre-F4 behaviour,
 * where a `(a AND b AND c)` group clumped into a single tiny text editor).
 *
 * The tint colour follows the group's connector (blue = AND, amber = OR),
 * matching the Rail view's palette, so the user sees at a glance which
 * predicates the parentheses bind together. Leaves reuse `RailLeafChip`
 * (path-addressed edit + IN-subquery drill); nested explicit groups recurse;
 * implicit precedence subgroups render transparently (no extra tint), exactly
 * the way `flattenBooleanTree` flattens them.
 *
 * F4: VQE compact/inline WHERE grouping.
 * The box ends with a "+ or" / "+ and" chip (the group's own connector) that
 * adds one more condition INTO the group; it shows on hover / focus of the
 * box (`ConditionChipSlot`).
 *
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testids and is threaded into `RailLeafChip`/`RailAddCondition` so a HAVING
 * inline group targets the HAVING tree (not just cosmetic — without `clause`
 * these child edits would silently target the wrong clause).
 */

import { type ReactElement, type ReactNode } from 'react';

import type { BooleanNode } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { vqeCopy } from '../../ux/vqeCopy';
import { ConditionChipSlot } from './ConditionChipSlot';
import { RailAddCondition } from './RailAddCondition';
import { WhereConnectorChip } from './WhereConnectorChip';
import { RailLeafChip, type RailSubquery } from './RailLeafChip';

type GroupNode = BooleanNode & { kind: 'group' };

const GROUP_TINT: Record<'AND' | 'OR', string> = {
  AND: 'border-blue-500/30 bg-blue-500/10',
  OR: 'border-amber-500/30 bg-amber-500/10',
};

const CONNECTOR_TINT: Record<'AND' | 'OR', string> = {
  AND: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  OR: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
};

/** Muted connector chip styling for the TOP-LEVEL connector before a group —
 *  matches `FilterPills`' inter-pill connector so the two read consistently. */
const MUTED_CONNECTOR =
  'rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground';

interface CommonProps {
  readonly editContext?: VqeEditContext;
  readonly subquery?: RailSubquery;
  readonly kind?: BooleanClauseTarget;
}

function ColoredConnector({
  connector,
  kind = 'where',
  groupPath,
  editContext,
}: {
  readonly connector: 'AND' | 'OR';
  readonly kind?: BooleanClauseTarget;
  /** Group the connector belongs to — makes the chip an AND/OR dropdown. */
  readonly groupPath?: readonly number[];
  readonly editContext?: VqeEditContext;
}): ReactElement {
  return (
    <WhereConnectorChip
      connector={connector}
      testId={`vqe-${kind}-inline-connector`}
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${CONNECTOR_TINT[connector]}`}
      kind={kind}
      groupPath={groupPath}
      editContext={editContext}
      markConnector
    />
  );
}

// Render one node inline, threading its leading connector and the path of the
// group that connector belongs to (`leadOwner`). Transparent through implicit
// precedence groups (their children render inline, threading the implicit
// connector); a leaf or explicit group renders as one pill preceded by its
// colored connector chip.
function renderInlineNodes(
  node: BooleanNode,
  path: readonly number[],
  lead: 'AND' | 'OR' | undefined,
  leadOwner: readonly number[] | undefined,
  props: CommonProps,
): ReactNode[] {
  if (node.kind === 'group' && !node.explicit) {
    return node.children.flatMap((child, idx) =>
      renderInlineNodes(
        child,
        [...path, idx],
        idx === 0 ? lead : node.connector,
        idx === 0 ? leadOwner : path,
        props,
      ),
    );
  }
  const pathKey = path.join('-') || 'root';
  const pill =
    node.kind === 'leaf' ? (
      <RailLeafChip
        node={node}
        path={path}
        editContext={props.editContext}
        subquery={props.subquery}
        kind={props.kind}
      />
    ) : (
      <InlineWhereGroup
        node={node}
        path={path}
        editContext={props.editContext}
        subquery={props.subquery}
        kind={props.kind}
      />
    );
  return [
    <span key={pathKey} className="inline-flex flex-wrap items-center gap-1.5">
      {lead ? (
        <ColoredConnector
          connector={lead}
          kind={props.kind}
          groupPath={leadOwner}
          editContext={props.editContext}
        />
      ) : null}
      {pill}
    </span>,
  ];
}

export function InlineWhereGroup({
  node,
  path,
  editContext,
  subquery,
  kind = 'where',
}: {
  readonly node: GroupNode;
  readonly path: readonly number[];
} & CommonProps): ReactElement {
  const pathKey = path.join('-') || 'root';
  const or = node.connector === 'OR';
  return (
    <span
      className={`relative inline-flex flex-wrap items-center gap-1.5 rounded-md border px-1.5 py-1 ${GROUP_TINT[node.connector]}`}
      data-testid={`vqe-${kind}-inline-group-${pathKey}`}
      data-chip-owner=""
      data-connector={node.connector}
      data-negated={node.negated ? 'true' : 'false'}
    >
      {node.negated ? (
        <span className="text-[10px] font-semibold uppercase text-muted-foreground">NOT</span>
      ) : null}
      {node.children.flatMap((child, idx) =>
        renderInlineNodes(
          child,
          [...path, idx],
          idx === 0 ? undefined : node.connector,
          idx === 0 ? undefined : path,
          { editContext, subquery, kind },
        ),
      )}
      {/* (A) Per-group add: append a constraint to THIS group, joined by the
          group's own connector. Only when editing is wired. */}
      {editContext ? (
        <ConditionChipSlot>
          {(onDraftOpenChange) => (
            <RailAddCondition
              groupPath={path}
              connector={node.connector}
              editContext={editContext}
              label={or ? vqeCopy.conditionChip.or : vqeCopy.conditionChip.and}
              triggerVariant="chip"
              hint={or ? vqeCopy.hint.addToOrGroup : vqeCopy.hint.addToAndGroup}
              onDraftOpenChange={onDraftOpenChange}
              kind={kind}
            />
          )}
        </ConditionChipSlot>
      ) : null}
    </span>
  );
}

/**
 * Top-level `<li>` wrapper for an explicit group inside the Inline pills `<ul>`.
 * Keeps the `vqe-where-pill-{idx}` / `vqe-having-pill-{idx}` testid contract at
 * this slot (so existing "pill N visible" assertions still hold) while
 * expanding the group's interior into separate, tinted, editable sub-pills.
 */
export function InlineWhereGroupPill({
  node,
  path,
  leadingConnector,
  testId,
  editContext,
  subquery,
  kind = 'where',
}: {
  readonly node: GroupNode;
  readonly path: readonly number[];
  readonly leadingConnector?: 'AND' | 'OR' | null;
  readonly testId: string;
} & CommonProps): ReactElement {
  return (
    <li className="flex items-center gap-1.5" data-testid={testId} data-has-group="true">
      {leadingConnector ? (
        <WhereConnectorChip
          connector={leadingConnector}
          testId={`${testId}-connector`}
          className={MUTED_CONNECTOR}
          kind={kind}
          // This group's leading connector belongs to its parent group.
          groupPath={path.slice(0, -1)}
          editContext={editContext}
        />
      ) : null}
      <InlineWhereGroup
        node={node}
        path={path}
        editContext={editContext}
        subquery={subquery}
        kind={kind}
      />
    </li>
  );
}
